<template>
  <div class="camp-completed">
    <img class="done-bg" src="@/assets/images/researchCamp/xueyuan_bg.png" alt="">
    <div class="done-body">
    <!-- 当前任务 -->
    <div class="done-head">
      <div class="exam-mark" v-if="type !== 'course'"><span>{{ type === 'exam' ? '考试' : '打卡' }}</span></div>
      <img class="done-cover" v-else :src="courseCover" :title="head.title" alt="">
      <div class="done-main">
        <div class="done-title">{{ head.title }}</div>
        <div class="done-line" v-for="line in head.lines" :key="line">{{ line }}</div>
      </div>
    </div>
    <div class="done-state" v-if="loading">加载中...</div>
    <div class="done-state" v-else-if="!loadError && !students.length">暂无已完成学员</div>
    <!-- 学员卡片 -->
    <camp-person-card
      v-for="item in students"
      :key="item.id"
      :item="item"
      align-top
      :arrow="false"
      @open="goArchive(item)"
    ></camp-person-card>
    </div>
  </div>
</template>

<script>
import CampPersonCard from './components/CampPersonCard.vue'
import withRouteTracking from '@/utils/withRouteTracking'
import {
  getResearchCampMembers,
  getResearchCampPlanItemStatistics,
  getResearchCampDispatchDetail,
  getResearchCampDispatchCompletedMembers
} from '@/api/researchCamp'
import { formatCampDate } from './date'

const CampCompleted = {
  name: 'CampCompleted',
  components: {
    CampPersonCard
  },
  data () {
    return {
      students: [],
      planItemDetail: {},
      dispatchDetail: {},
      loading: false,
      loadError: ''
    }
  },
  computed: {
    // 已完成列表类型：课程、考试或打卡
    type () {
      const query = this.$route.query
      if (query.type) return query.type
      if (query.itemType) return String(query.itemType).toLowerCase()
      if (Number(this.dispatchDetail.contentType) === 2) return 'exam'
      if (Number(this.dispatchDetail.contentType) === 3) return 'message'
      return 'course'
    },
    // 课程封面兼容旧链接，并优先使用重新查询的计划项或派发详情。
    courseCover () {
      return this.$route.query.cover || this.planItemDetail.coverUrl || this.dispatchDetail.coverUrl ||
        require('@/assets/images/researchCamp/banner.png')
    },
    // 页头：课程显示医院和讲师，考试和打卡显示已完成人数
    head () {
      const query = this.$route.query
      const title = query.title || this.planItemDetail.name || this.dispatchDetail.name || ''
      const completedCount = query.completedLearnerCount || this.planItemDetail.completedLearnerCount ||
        this.dispatchDetail.completedMemberCount || 0
      if (this.type === 'exam') {
        return {
          title: title || '考试',
          lines: [`已完成${completedCount}人`]
        }
      }
      if (this.type === 'checkin') {
        const name = query.taskType || this.planItemDetail.taskType || title
        return {
          title: name || '打卡',
          lines: [`已完成${completedCount}人`]
        }
      }
      return {
        title: title || '课程',
        lines: [
          query.hospital || this.planItemDetail.courseHospitalName || this.dispatchDetail.courseHospitalName
            ? `医院：${query.hospital || this.planItemDetail.courseHospitalName || this.dispatchDetail.courseHospitalName}` : '',
          query.teacher || this.planItemDetail.courseLecturerNames || this.dispatchDetail.courseLecturerNames
            ? `讲师：${query.teacher || this.planItemDetail.courseLecturerNames || this.dispatchDetail.courseLecturerNames}` : ''
        ].filter(Boolean)
      }
    }
  },
  created () {
    this.loadCompletedMembers()
  },
  methods: {
    // 加载当前计划项或定制任务的已完成学员
    async loadCompletedMembers () {
      const query = this.$route.query
      const campConfigId = query.campConfigId
      const planItemId = query.planItemId
      const dispatchId = query.dispatchId
      if ((planItemId && !campConfigId) || (!planItemId && !dispatchId)) {
        this.loadError = '缺少研修营或任务信息'
        this.$toast(this.loadError)
        return
      }
      this.loading = true
      this.loadError = ''
      try {
        let response
        if (planItemId) {
          const viewRole = query.viewRole || 'CAMP_DIRECTOR'
          const statisticsQuery = {
            viewRole,
            itemType: query.itemType || this.type.toUpperCase()
          }
          if (query.viewScope || viewRole === 'PD') statisticsQuery.viewScope = query.viewScope || 'CAMP'
          try {
            const statisticsResponse = await getResearchCampPlanItemStatistics(campConfigId, statisticsQuery)
            const statistics = statisticsResponse && Array.isArray(statisticsResponse.data)
              ? statisticsResponse.data : []
            this.planItemDetail = statistics.find(item => String(item.planItemId) === String(planItemId)) || {}
          } catch (error) {
            this.planItemDetail = {}
          }
          response = await getResearchCampMembers(campConfigId, { planItemId })
        } else {
          try {
            const detailResponse = await getResearchCampDispatchDetail(dispatchId)
            this.dispatchDetail = detailResponse && detailResponse.data ? detailResponse.data : {}
          } catch (error) {
            this.dispatchDetail = {}
          }
          response = await getResearchCampDispatchCompletedMembers(dispatchId)
        }
        const rows = response && Array.isArray(response.data) ? response.data : []
        this.students = rows.map(item => ({
          id: item.campMemberId,
          campConfigId: item.campConfigId || campConfigId,
          name: item.name || '',
          level: item.currentIepLevel || item.entryIepLevel || '',
          center: item.campName || '',
          hospital: item.hospitalName || '',
          department: item.departmentL1 || '',
          title: item.positionTitle || '',
          joinDate: formatCampDate(item.entryTime),
          badge: this.type === 'exam' && item.latestSubmittedScore !== null && item.latestSubmittedScore !== undefined
            ? String(item.latestSubmittedScore)
            : this.type === 'checkin' && item.checkinIntervalDays !== null && item.checkinIntervalDays !== undefined
              ? `${item.checkinIntervalDays}天` : '',
          extra: this.type === 'checkin' && item.checkinDate ? `打卡日期：${formatCampDate(item.checkinDate)}` : ''
        }))
      } catch (error) {
        this.loadError = error && error.msg ? error.msg : '已完成学员加载失败'
        this.$toast(this.loadError)
      } finally {
        this.loading = false
      }
    },
    // 进入该学员的培训档案
    goArchive (item) {
      this.$router.push({
        path: '/researchCamp/archive',
        query: {
          studentId: item.id,
          campConfigId: item.campConfigId || this.$route.query.campConfigId,
          viewRole: this.$route.query.viewRole
        }
      })
    }
  }
}

export default withRouteTracking(CampCompleted)
</script>

<style lang="less" scoped>
@import './camp.less';

.camp-completed {
  .camp-page();
  position: relative;
  padding: 14px 0 24px;

  .done-bg {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 139px;
    object-fit: cover;
    object-position: right center;
    pointer-events: none;
  }

  .done-body {
    position: relative;
    z-index: 1;
  }

  .done-state {
    padding: 22px 14px;
    text-align: center;
    font-size: 12px;
    color: @camp-sub;
  }

  .done-head {
    display: flex;
    align-items: flex-start;
    margin: 0 13px;
    padding: 12px 14px;
    border-radius: 10px;
    background: #fff;
    box-sizing: border-box;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
  }

  .done-cover {
    width: 100px;
    height: 58px;
    margin-right: 10px;
    border-radius: 6px;
    object-fit: cover;
    flex-shrink: 0;
  }

  .exam-mark {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    margin-right: 12px;
    border-radius: 8px;
    background: #ff6017;
    flex-shrink: 0;

    span {
      font-size: 14px;
      font-weight: 500;
      line-height: 20px;
      color: #fff;
    }
  }

  .done-main {
    flex: 1;
    min-width: 0;
  }

  .done-title {
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    color: #000;
    word-break: break-word;
  }

  .done-line {
    margin-top: 2px;
    font-size: 12px;
    font-weight: 400;
    line-height: 18px;
    color: @camp-sub;
    word-break: break-word;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/researchCamp/completed.vue